import { LitElement, css, html } from "lit";
import { onceParentAvailable } from "../../utils/widgets.js";
import { MAP_THEMES_STYLES } from "../../utils/map.js";
import { COLORS, QUALITYSCORE_VALUES } from "../../utils/utils.js";

/**
 * Map Theme menu allows user to select map colouring scheme.
 * @class Panoramax.components.menus.MapTheme
 * @element pnx-map-theme
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-map-theme _parent=${viewer} />
 * ```
 */
export default class MapTheme extends LitElement {
	/** @private */
	static styles = css`
		:host {
			justify-content: space-evenly;
			box-sizing: border-box;
			display: flex;
			position: relative;
		}
		input { display: none; }
		label {
			cursor: pointer;
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 5px;
			font-size: 0.7rem;
			line-height: 0.8rem;
			color: var(--grey-dark);
			width: 50px;
			text-align: center;
			word-break: break-word;
			margin-right: 10px;
		}
		label:last-child { margin-right: unset; }

		.colors-scheme {
			width: 40px;
			height: 40px;
			border-radius: 22px;
			vertical-align: middle;
			border: 2px solid var(--widget-bg);
			display: flex;
			flex-direction: row;
			overflow: hidden;
		}

		@media screen and (max-width: 576px) {
			label { font-size: 0.9rem; }
		}

		.colors-scheme .color-entry {
			display: block;
			width: 100%;
			height: 100%;
		}

		input:checked + label .colors-scheme {
			outline: 3px solid var(--widget-border-btn);
		}

		.pnx-theme-zoomin {
			z-index: 131;
			background-color: rgba(255,255,255,0.9);
			position: absolute;
			top: -10px;
			right: 0;
			left: 0;
			bottom: 0;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: baseline;
		}
		.pnx-theme-zoomin h5 {
			font-weight: 900;
			margin: 0 0 5px 0;
			font-size: 1em;
		}
		.pnx-theme-zoomin div {
			font-weight: unset;
  			font-size: 0.9em;
		}
		.pnx-theme-zoomin.hidden {
			display: none;
		}
	`;

	/** @private */
	static properties = {
		theme: {state: true},
	};
	
	/** @private */
	connectedCallback() {
		super.connectedCallback();

		onceParentAvailable(this).then(() => this._parent?.onceMapReady?.().then(() => {
			this.requestUpdate();
			this._parent.mapStyleComposer?.addEventListener("theme-changed", e => {this.theme = e.detail.theme; this.requestUpdate(); });
			this._parent.mapStyleComposer?.addEventListener("panoramax-changed", () => setTimeout(this.requestUpdate.bind(this), 1000));
			this._parent.mapStyleComposer?.addEventListener("filters-changed", () => this.requestUpdate());
			this._parent.map?.on("zoomend", () => this.requestUpdate());
			this.theme = this._parent.mapStyleComposer.panoramaxTheme;
		}));
	}

	/** @private */
	_onThemeSelect(e) {
		this.theme = e.target.value;
		this._parent?.mapStyleComposer.setPanoramaxTheme(this.theme);
	}

	/** @private */
	render() {
		const isSmall = this._parent?.isWidthSmall();
		const hasGridStats = this._parent?.map?._hasGridStats?.() || false;
		const showZoomIn = this._parent.mapStyleComposer._hideMapFilters();
		
		return html`
			<div class="pnx-theme-zoomin ${showZoomIn ? "" : "hidden"}">
				<h5>${this._parent?._t.pnx.widget_locked}</h5>
				<div>${this._parent?._t.pnx.filter_zoom_in}</div>
			</div>
			<input
				type="radio"
				id="pnx-map-theme-default"
				name="pnx-map-theme"
				value=${MAP_THEMES_STYLES.default.id}
				@change=${this._onThemeSelect}
				.checked=${this.theme === MAP_THEMES_STYLES.default.id}
			/>
			<label for="pnx-map-theme-default">
				<div class="colors-scheme">
					${hasGridStats ? html`
						<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.default.color[1]}"></span>
						<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.default.color[3]}"></span>
						<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.default.color[5]}"></span>
						<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.default.color[7]}"></span>
					` : html`
						<span class="color-entry" style="background-color: ${COLORS.BASE}"></span>
					`}
				</div>
				${hasGridStats ?
					(isSmall ? this._parent?._t.pnx.map_theme_default_coef_short : this._parent?._t.pnx.map_theme_default_coef)
					: (isSmall ? this._parent?._t.pnx.map_theme_default_short : this._parent?._t.pnx.map_theme_default)
				}
			</label>

			<input
				type="radio"
				id="pnx-map-theme-age"
				name="pnx-map-theme"
				value=${MAP_THEMES_STYLES.age.id}
				@change=${this._onThemeSelect}
				.checked=${this.theme === MAP_THEMES_STYLES.age.id}
			/>
			<label for="pnx-map-theme-age">
				<div class="colors-scheme">
					<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.age.color[3]}"></span>
					<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.age.color[5]}"></span>
					<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.age.color[7]}"></span>
					<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.age.color[8]}"></span>
				</div>
				${isSmall ? this._parent?._t.pnx.map_theme_age_short : this._parent?._t.pnx.map_theme_age}
			</label>

			<input
				type="radio"
				id="pnx-map-theme-type"
				name="pnx-map-theme"
				value=${MAP_THEMES_STYLES.type.id}
				@change=${this._onThemeSelect}
				.checked=${this.theme === MAP_THEMES_STYLES.type.id}
			/>
			<label for="pnx-map-theme-type">
				<div class="colors-scheme">
					<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.type.color[3]}"></span>
					<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.type.color[4]}"></span>
				</div>
				${isSmall ? this._parent?._t.pnx.map_theme_type_short : this._parent?._t.pnx.map_theme_type}
			</label>

			${this._parent?.map?._hasQualityScore?.() && html`
				<input
					type="radio"
					id="pnx-map-theme-score"
					name="pnx-map-theme"
					value=${MAP_THEMES_STYLES.score.id}
					@change=${this._onThemeSelect}
					.checked=${this.theme === MAP_THEMES_STYLES.score.id}
				/>
				<label for="pnx-map-theme-score">
					<div class="colors-scheme">
						${QUALITYSCORE_VALUES.map(qv => html`
							<span class="color-entry" style="background-color: ${qv.color}"></span>
						`)}
					</div>
					${isSmall ? this._parent?._t.pnx.map_theme_score_short : this._parent?._t.pnx.map_theme_score}
				</label>

				<input
					type="radio"
					id="pnx-map-theme-gps"
					name="pnx-map-theme"
					value=${MAP_THEMES_STYLES.gps.id}
					@change=${this._onThemeSelect}
					.checked=${this.theme === MAP_THEMES_STYLES.gps.id}
				/>
				<label for="pnx-map-theme-gps">
					<div class="colors-scheme">
						<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.gps.color[1]}"></span>
						<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.gps.color[3]}"></span>
						<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.gps.color[5]}"></span>
						<span class="color-entry" style="background-color: ${MAP_THEMES_STYLES.gps.color[6]}"></span>
					</div>
					${isSmall ? this._parent?._t.pnx.map_theme_gps_short : this._parent?._t.pnx.map_theme_gps}
				</label>
			`}
		`;
	}
}

customElements.define("pnx-map-theme", MapTheme);
